/**
 * Mapping from Web template keys to Widget SDK widget IDs
 * These map the template keys used in TemplatesSidebar to the widget IDs in window.BidgelyWebSdk.WIDGET_TAG
 */

type WidgetTagKey = 
  | "INSIGHTS_USAGE"
  | "INSIGHTS_SIMILAR_HOMES"
  | "RATE_PLAN_COMPARISON"
  | "INSIGHTS_MONTHLY_SUMMARY"
  | "RECOMMENDATION_TOP_TIPS"
  | "RECOMMENDATION_TIPS_LIBRARY"
  | "RECOMMENDATION_TOP_PROGRAMS"
  | "BILL_PROJECTION"
  | "CARE_ENGAGEMENT_VIEW"
  | "SURVEY"
  | "HOME_PROFILE_SURVEY"
  | "HIGH_BILL_ANALYSER"
  | "HIGH_BILL_ANALYSER_V3"
  | "USER_PREFERENCES"
  | "GB_DOWNLOAD"
  | "INSIGHTS_ACTIVITY_MAP"
  | "FAQ"
  | "CHATBOT"
  | "NOTIFICATIONS"
  | "BILL_SUMMARY"
  | "BILL_SUMMARY_V2"
  | "WEATHER"
  | "CONSUMPTION_BREAKDOWN"
  | "USAGE_HISTORY"
  | "INSIGHTS_FEED"
  | "SMART_RECOMMENDATION"
  | "ENERGY_FLOW"
  | "ENVIRONMENT"
  | "SURVEY_PROMPT_BAR"
  | "PROMOTIONAL_CONTENT"
  | "FOOTER_MAIN"
  | "SAVINGS_HUB";

export const WEB_TEMPLATE_TO_WIDGET_MAP: Record<string, WidgetTagKey> = {
  // Existing mappings
  // 'WEB_MONTHLY_SUMMARY': 'INSIGHTS_MONTHLY_SUMMARY',
  // 'WEB_SIMILAR_HOMES': 'INSIGHTS_SIMILAR_HOMES',
  // 'WEB_MY_RECOS': 'RECOMMENDATION_TOP_TIPS',
  // 'WEB_ENERGY_DETAILS': 'INSIGHTS_USAGE',
  // 'WEB_BILL_ANALYSIS': 'BILL_SUMMARY',
  // 'WEB_HOME_SURVEY': 'HOME_PROFILE_SURVEY',
  // 'WEB_PREFERENCES': 'USER_PREFERENCES',
  // 'WEB_DOWNLOAD_MY_USAGE': 'GB_DOWNLOAD',
  // 'WEB_MY_NOTIFICATIONS': 'NOTIFICATIONS',
  // 'DASHBOARD.ELECTRIC': 'INSIGHTS_USAGE', // Default widget for dashboard
  // // Additional widget mappings
  // 'WEB_RATE_PLAN_COMPARISON': 'RATE_PLAN_COMPARISON',
  // 'WEB_TIPS_LIBRARY': 'RECOMMENDATION_TIPS_LIBRARY',
  // 'WEB_TOP_PROGRAMS': 'RECOMMENDATION_TOP_PROGRAMS',
  // 'WEB_BILL_PROJECTION': 'BILL_PROJECTION',
  // 'WEB_CARE_ENGAGEMENT': 'CARE_ENGAGEMENT_VIEW',
  // 'WEB_SURVEY': 'SURVEY',
  // 'WEB_HIGH_BILL_ANALYSER': 'HIGH_BILL_ANALYSER',
  // 'WEB_ACTIVITY_MAP': 'INSIGHTS_ACTIVITY_MAP',
  // 'WEB_FAQ': 'FAQ',
  // 'WEB_BILL_SUMMARY_V2': 'BILL_SUMMARY_V2',
  // 'WEB_CHATBOT': 'CHATBOT',
  // 'WEB_HIGH_BILL_ANALYSER_V3': 'HIGH_BILL_ANALYSER_V3',
  // 'WEB_SAVINGS_HUB': 'SAVINGS_HUB',
  // // Mock template mappings (TODO: Remove when real templates are available)
  // // Only include mappings that don't already exist above
  // 'WEB_INSIGHTS_USAGE': 'INSIGHTS_USAGE',
  // 'WEB_INSIGHTS_SIMILAR_HOMES': 'INSIGHTS_SIMILAR_HOMES',
  // 'WEB_INSIGHTS_MONTHLY_SUMMARY': 'INSIGHTS_MONTHLY_SUMMARY',
  // 'WEB_INSIGHTS_ACTIVITY_MAP': 'INSIGHTS_ACTIVITY_MAP',
  // 'WEB_RECOMMENDATION_TOP_TIPS': 'RECOMMENDATION_TOP_TIPS',
  // 'WEB_RECOMMENDATION_TIPS_LIBRARY': 'RECOMMENDATION_TIPS_LIBRARY',
  // 'WEB_RECOMMENDATION_TOP_PROGRAMS': 'RECOMMENDATION_TOP_PROGRAMS',
  // 'WEB_BILL_SUMMARY': 'BILL_SUMMARY', // Note: WEB_BILL_ANALYSIS maps to BILL_SUMMARY, this is a different key
  // 'WEB_CARE_ENGAGEMENT_VIEW': 'CARE_ENGAGEMENT_VIEW', // Note: WEB_CARE_ENGAGEMENT maps to this, this is a different key
  // 'WEB_HOME_PROFILE_SURVEY': 'HOME_PROFILE_SURVEY', // Note: WEB_HOME_SURVEY maps to this, this is a different key
  // 'WEB_USER_PREFERENCES': 'USER_PREFERENCES', // Note: WEB_PREFERENCES maps to this, this is a different key
  // 'WEB_GB_DOWNLOAD': 'GB_DOWNLOAD', // Note: WEB_DOWNLOAD_MY_USAGE maps to this, this is a different key
  // 'WEB_NOTIFICATIONS': 'NOTIFICATIONS', // Note: WEB_MY_NOTIFICATIONS maps to this, this is a different key
  // Dashboard template mappings
  'DASHBOARD.HOME.BILL_ITEMIZATION': 'BILL_SUMMARY',
  'DASHBOARD.HOME.INSIGHTS_AND_TRENDS': 'INSIGHTS_USAGE',
  'DASHBOARD.HOME': 'INSIGHTS_USAGE', // Default widget for dashboard home
  'DASHBOARD.MY_RECOMMENDATIONS': 'RECOMMENDATION_TOP_TIPS',
  'DASHBOARD.HOME.BILL_PROJECTION': 'BILL_PROJECTION',
  'DASHBOARD.SURVEY': 'SURVEY',
  'DASHBOARD.HOME.BILL_SUMMARY': 'BILL_SUMMARY',
  'DASHBOARD.ENERGY_INSIGHTS.BILL_ANALYSIS': 'BILL_SUMMARY',
  'DASHBOARD.HOME.CARBON_WIDGET': 'ENVIRONMENT',
  'MULTIPREMISE': 'INSIGHTS_USAGE', // Default widget for multipremise
  'DASHBOARD.HOME.FOOTER': 'FOOTER_MAIN',
  'DASHBOARD.FAQS': 'FAQ',
  'FEEDBACK': 'CARE_ENGAGEMENT_VIEW', // Feedback likely maps to engagement view
  'DASHBOARD.ENERGY_INSIGHTS.USAGE': 'INSIGHTS_USAGE',
  'DASHBOARD.ENERGY_INSIGHTS.MONTHLY_SUMMARY': 'INSIGHTS_MONTHLY_SUMMARY',
  'DASHBOARD.ENERGY_INSIGHTS.SHC': 'INSIGHTS_SIMILAR_HOMES', // SHC = Similar Homes Comparison
  'DASHBOARD.HOME.BILL_HISTORY': 'USAGE_HISTORY',
  'DASHBOARD.ENERGY_INSIGHTS.RATE_PLAN': 'RATE_PLAN_COMPARISON',
  'DASHBOARD.SETTINGS_AND_PREFERENCES.ALERT_TYPE': 'USER_PREFERENCES',
  'DASHBOARD.GB_DOWNLOAD': 'GB_DOWNLOAD',
  'ENGAGEMENT_VIEW': 'CARE_ENGAGEMENT_VIEW',
  'DASHBOARD.ENERGY_INSIGHTS.ACTIVITY_MAP': 'INSIGHTS_ACTIVITY_MAP',
  'DASHBOARD.NOTIFICATIONS': 'NOTIFICATIONS'
};

/**
 * Check if a template key is a Web widget template
 */
export const isWebWidgetTemplate = (templateKey: string | null): boolean => {
  if (!templateKey) return false;
  // Check if it's in the mapping, starts with WEB_ or DASHBOARD., or is a mock template
  return templateKey in WEB_TEMPLATE_TO_WIDGET_MAP || 
         templateKey.startsWith('WEB_') || 
         templateKey.startsWith('DASHBOARD.');
};

/**
 * Get widget ID from template key
 */
export const getWidgetIdFromTemplate = (templateKey: string | null): WidgetTagKey | null => {
  if (!templateKey) return null;
  return WEB_TEMPLATE_TO_WIDGET_MAP[templateKey] || null;
};

